import type { FC } from "react";
import { useApolloClient } from "@apollo/client";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { CrudLink } from "@/shared/components";
import { PagePaths } from "@/admin/shared/constants";
import type { ContractPaymentShowData } from "@/admin/entities/contractPayments";
import { ContractPaymentActions } from "./ContractPaymentActions";
import { ContractPaymentLines } from "./ContractPaymentLines";
import { formatDay, formatInvoiceRate, formatMoney } from "./billingFormat";
import { renderContractPaymentStatus } from "./billingLabels";
import {
  invoiceDownloadUrl,
  overdueDays,
  overdueLabel,
} from "./contractPaymentCardHelpers";
import { GRID_SX } from "./infoGrid";
import { InfoItem } from "./InfoItem";

interface ContractPaymentCardProps {
  /** CrudShow разворачивает ответ showQuery в пропсы компонента. */
  contractPayment?: ContractPaymentShowData | null;
  /** id платежа CrudShow передаёт отдельным пропом; здесь не нужен. */
  id?: string;
}

/** Карточка и список берут платёж из разных запросов: после действия оба. */
const REFRESH_QUERIES = ["ContractPaymentShow", "ContractPaymentsList"];

/** «К оплате 184,68 EUR. Курс ЕЦБ ...» одной строкой, без курса только сумма. */
function invoiceAmountCaption(payment: ContractPaymentShowData): string {
  if (!payment.invoiceAmount) return "";
  const rate = formatInvoiceRate(
    payment.amount.currency,
    payment.invoiceExchangeRate,
    payment.invoiceAmount.currency
  );
  const amount = `К оплате ${formatMoney(payment.invoiceAmount)}`;
  return rate ? `${amount}. ${rate}` : amount;
}

/**
 * Карточка платежа по контракту (спека 2026-10-16 §4.1): сведения,
 * строки и итог, блок счёта с историей перевыставлений, панель действий
 * ContractPaymentActions плюс «Скачать PDF» и «Открыть счёт».
 */
export const ContractPaymentCard: FC<ContractPaymentCardProps> = ({
  contractPayment: payment,
}) => {
  const client = useApolloClient();

  if (!payment) {
    return (
      <Stack sx={{ p: 2 }}>
        <Typography variant="h6" color="error">
          Платёж не найден
        </Typography>
      </Stack>
    );
  }

  const refresh = () => {
    void client.refetchQueries({ include: REFRESH_QUERIES });
  };
  const days = payment.expired ? overdueDays(payment.expiredAt) : 0;
  const invoice = payment.invoice;
  const caption = invoiceAmountCaption(payment);

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <Card>
        <CardContent>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            sx={{ justifyContent: "space-between", mb: 2 }}
          >
            <Stack spacing={1} sx={{ alignItems: "flex-start" }}>
              <Typography variant="h5" component="h2">
                {formatMoney(payment.amount)}
              </Typography>
              {caption && (
                <Typography variant="body2" color="text.secondary">
                  {caption}
                </Typography>
              )}
              {renderContractPaymentStatus(payment.status, payment.expired)}
            </Stack>
            {payment.invoiceUrl && (
              <Stack
                direction="row"
                spacing={1}
                sx={{ flexWrap: "wrap", rowGap: 1, alignItems: "flex-start" }}
              >
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  href={invoiceDownloadUrl(payment.invoiceUrl)}
                >
                  Скачать PDF
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<OpenInNewIcon />}
                  href={payment.invoiceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Открыть счёт
                </Button>
              </Stack>
            )}
          </Stack>

          <Box sx={GRID_SX}>
            <InfoItem label="Организация">
              <CrudLink
                path={PagePaths.organizations}
                tab="details"
                id={payment.organizationId}
              >
                {payment.organization?.title ?? payment.organizationId}
              </CrudLink>
            </InfoItem>
            <InfoItem label="Контракт">
              <CrudLink
                path={PagePaths.billingContracts}
                tab="details"
                id={payment.contractId}
              >
                {payment.contract?.title ?? payment.contractId}
              </CrudLink>
            </InfoItem>
            <InfoItem label="Период">
              {`${formatDay(payment.periodFrom)}, ${formatDay(payment.periodTo)}`}
            </InfoItem>
            <InfoItem label="Дата счёта">
              {formatDay(payment.invoiceDate)}
            </InfoItem>
            <InfoItem label="Срок оплаты">
              <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                <span>{formatDay(payment.expiredAt)}</span>
                {days > 0 && (
                  <Chip size="small" color="error" label={overdueLabel(days)} />
                )}
              </Stack>
            </InfoItem>
            <InfoItem label="Оплачен">
              {payment.paidAt ? formatDay(payment.paidAt) : "Нет"}
            </InfoItem>
            {payment.amountInOrganizationCurrency && (
              <InfoItem label="В валюте организации">
                {formatMoney(payment.amountInOrganizationCurrency)}
              </InfoItem>
            )}
            {payment.note && (
              <InfoItem label="Заметка">{payment.note}</InfoItem>
            )}
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardHeader title="Строки платежа" />
        <CardContent sx={{ pt: 0 }}>
          <ContractPaymentLines lines={payment.lines} />
          <Stack
            direction="row"
            sx={{ justifyContent: "space-between", mt: 2 }}
          >
            <Typography variant="subtitle2">Итого</Typography>
            <Typography variant="subtitle2">
              {formatMoney(payment.amount)}
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardHeader title="Счёт" />
        <CardContent sx={{ pt: 0 }}>
          {invoice ? (
            <Stack spacing={2}>
              <Box sx={GRID_SX}>
                <InfoItem label="Номер">{invoice.number}</InfoItem>
                <InfoItem label="Выставлен">
                  {formatDay(invoice.issuedAt)}
                </InfoItem>
                <InfoItem label="Отправлен">
                  {invoice.sentAt
                    ? `${formatDay(invoice.sentAt)} на ${invoice.sentTo.join(", ")}`
                    : "Не отправлялся"}
                </InfoItem>
              </Box>
              {invoice.previousNumbers.length > 0 && (
                <Stack>
                  <Typography variant="subtitle2" color="text.secondary">
                    Перевыставления
                  </Typography>
                  <List dense disablePadding>
                    {invoice.previousNumbers.map((item) => (
                      <ListItem key={item.number} disableGutters>
                        <ListItemText
                          primary={`№${item.number} от ${formatDay(item.issuedAt)}`}
                          secondary={item.reason}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Stack>
              )}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Счёт ещё не выставлен
            </Typography>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader title="Действия" />
        <CardContent sx={{ pt: 0 }}>
          <ContractPaymentActions
            payment={payment}
            onCompleted={refresh}
            emptyText="Для этого статуса действий нет"
          />
        </CardContent>
      </Card>
    </Stack>
  );
};
